import GroupAdd from "@mui/icons-material/GroupAdd";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from "@mui/material";

import { CoAppActionButton, CoAppCancelTextButton, CoAppCloseIcon } from "../../../global/styled/global.styled";

interface SsoScimWizardPreConfigurationModalProps {
	dialogOpen: boolean;
	confirmationTitle?: string;
	confirmationMessage?: string;
	confirmationListItems?: string[];
	confirmClickHandler: () => void;
	cancelClickHandler: () => void;
}

export default function SsoScimWizardPreConfigurationModal({
	dialogOpen,
	confirmationTitle = "Configure SSO + SCIM",
	confirmClickHandler,
	cancelClickHandler,
}: SsoScimWizardPreConfigurationModalProps) {
	return (
		<Dialog
			open={dialogOpen}
			onClose={cancelClickHandler}
			maxWidth="sm"
			fullWidth
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						overflow: "hidden",
					},
				},
			}}
		>
			<DialogTitle
				sx={{
					px: 3,
					pt: 3,
					pb: 1,
					display: "flex",
					alignItems: "center",
					gap: 1.25,
				}}
			>
				<Box
					sx={{
						width: 36,
						height: 36,
						borderRadius: 1.5,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(2, 213, 171, 0.10)",
						color: "#057A6D",
					}}
				>
					<GroupAdd fontSize="small" />
				</Box>
				<Stack sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						sx={{
							fontSize: 18,
							fontWeight: 700,
							color: "rgba(13, 31, 48, 0.92)",
							letterSpacing: "-0.01em",
							lineHeight: 1.2,
						}}
					>
						{confirmationTitle}
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "rgba(13, 31, 48, 0.55)",
							lineHeight: 1.4,
							mt: 0.25,
						}}
					>
						A quick heads-up before you switch this on.
					</Typography>
				</Stack>
			</DialogTitle>

			<CoAppCloseIcon onClick={cancelClickHandler} />

			<DialogContent sx={{ px: 3, py: 1.5 }}>
				<Stack spacing={2.5}>
					{/* SECTION — what changes */}
					<Stack spacing={1}>
						<Typography
							sx={{
								fontSize: 12,
								fontWeight: 700,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								color: "rgba(13, 31, 48, 0.55)",
							}}
						>
							What changes
						</Typography>
						<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.85)", lineHeight: 1.55 }}>
							User management moves to your identity provider. You'll add, remove, and
							assign users to groups <strong>in your IdP</strong>, not in CoApp.
						</Typography>
						<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.85)", lineHeight: 1.55 }}>
							Existing groups and roles stay put, but anyone currently assigned to them
							will be <strong>unassociated</strong> once SCIM setup finishes.
						</Typography>
					</Stack>

					{/* CALLOUT — destructive consequence */}
					<Box
						sx={{
							display: "flex",
							gap: 1.25,
							p: 1.75,
							borderRadius: 2,
							background: "rgba(232, 80, 91, 0.06)",
							border: "1px solid rgba(232, 80, 91, 0.25)",
						}}
					>
						<WarningAmberRounded
							sx={{ color: "#B81E15", fontSize: 22, flexShrink: 0, mt: 0.25 }}
						/>
						<Stack spacing={0.5} sx={{ minWidth: 0 }}>
							<Typography
								sx={{
									fontSize: 13,
									fontWeight: 700,
									color: "#B81E15",
									letterSpacing: "0.01em",
								}}
							>
								Users without a matching IdP assignment will be deleted
							</Typography>
							<Typography
								variant="body2"
								sx={{ color: "rgba(13, 31, 48, 0.85)", lineHeight: 1.5 }}
							>
								Any existing CoApp user who doesn't come over with a group or role
								from your IdP will be removed. Double-check your IdP groups before
								proceeding.
							</Typography>
						</Stack>
					</Box>

					{/* SECTION — after setup */}
					<Stack spacing={1}>
						<Typography
							sx={{
								fontSize: 12,
								fontWeight: 700,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								color: "rgba(13, 31, 48, 0.55)",
							}}
						>
							After setup
						</Typography>
						<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.85)", lineHeight: 1.55 }}>
							Users may need to sign in again, and it can take a few minutes for users
							and groups to propagate from your IdP into CoApp.
						</Typography>
					</Stack>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
				<CoAppCancelTextButton onClick={cancelClickHandler}>Cancel</CoAppCancelTextButton>
				<CoAppActionButton onClick={confirmClickHandler}>I understand, continue</CoAppActionButton>
			</DialogActions>
		</Dialog>
	);
}
